import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { getProductById, getCategories } from "../utils/api";
import { imgSrc } from "../utils/images";

export default function Item({ cart, setCart, currentUser }) {
  const { id } = useParams();
  const navigate = useNavigate();

  const [categories, setCategories] = useState([]);
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        const [data, cats] = await Promise.all([
          getProductById(id),
          getCategories(),
        ]);

        if (cancelled) return;

        setItem(data || null);
        setCategories(Array.isArray(cats) ? cats : []);
      } catch (e) {
        if (cancelled) return;
        setItem(null);
        setCategories([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  let cartCount = 0;
  const safeCart = Array.isArray(cart) ? cart : [];
  const found = safeCart.find((c) => String(c.id) === String(id));
  if (found) cartCount = Number(found.quantity || 0);

  function addToCart() {
    // must be logged in to buy
    if (!currentUser) {
      navigate("/login");
      return;
    }

    // only buyer/admin can add to cart
    const role = String(currentUser?.role || "").toLowerCase();
    const canBuy = role === "buyer" || role === "admin";
    if (!canBuy) return;

    // only allow available items
    const isAvailable =
      String(item?.status || "").toLowerCase() === "available";
    if (!isAvailable) {
      alert("This item is not available.");
      return;
    }

    const safe = Array.isArray(cart) ? [...cart] : [];
    const idx = safe.findIndex((c) => String(c.id) === String(id));

    if (idx >= 0) {
      safe[idx] = {
        ...safe[idx],
        quantity: Number(safe[idx].quantity || 0) + 1,
      };
    } else {
      safe.push({ id: Number(id), quantity: 1 });
    }

    setCart(safe);
    alert("Successfully Added to Cart!");
  }
  if (loading) {
    return (
      <div className="container pagePadBottom" style={{ maxWidth: 760 }}>
        <div className="card">
          <div style={{ fontWeight: 900, fontSize: 18 }}>Loading…</div>
          <div className="muted" style={{ marginTop: 6 }}>
            Fetching item details.
          </div>
        </div>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="container pagePadBottom" style={{ maxWidth: 760 }}>
        <div className="card">
          <div style={{ fontWeight: 900, fontSize: 20 }}>Item not found</div>
          <div className="muted" style={{ marginTop: 6 }}>
            It may have been removed.
          </div>
          <div className="row" style={{ marginTop: 12, flexWrap: "wrap" }}>
            <button className="btnSecondary" onClick={() => navigate(-1)}>
              Go back
            </button>
            <button className="btnPrimary" onClick={() => navigate("/")}>
              Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  const price = Number(item.price ?? 0);
  const condition = item.item_condition ?? item.condition ?? "";
  const status = item.status ?? "";
  const image = item.image_path ?? item.image ?? "";

  const categoryId = item.product_category_id ?? item.category_id ?? null;
  const cat = categories.find((c) => String(c.id) === String(categoryId));
  const categoryName = cat?.title ?? cat?.label ?? "";

  const sellerName = item?.seller_name || "";
  const sellerId = item.seller_user_id ?? item.user_id ?? "";
  const role = String(currentUser?.role || "").toLowerCase();
  const canBuy = role === "buyer" || role === "admin";
  const isAvailable = String(status || "").toLowerCase() === "available";

  const canEdit =
    role === "admin" ||
    (role === "seller" && String(currentUser?.id) === String(sellerId));

  return (
    <div className="container pagePadBottom">
      <div className="card">
        <div
          className="itemGrid"
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(260px, 360px) 1fr",
            gap: 16,
            alignItems: "start",
          }}
        >
          {/* Image */}
          <div>
            <div
              style={{
                width: "100%",
                aspectRatio: "4 / 3",
                borderRadius: 16,
                overflow: "hidden",
                border: "1px solid rgba(15, 23, 42, 0.1)",
                background: "#eef2ff",
              }}
            >
              <img
                src={imgSrc(image)}
                alt={item?.name || "Item"}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                }}
              />
            </div>
          </div>

          {/* Details */}
          <div style={{ minWidth: 0 }}>
            <div
              className="row"
              style={{
                marginTop: 10,
                gap: 8,
                flexWrap: "wrap",
                justifyContent: "space-between",
              }}
            >
              <div style={{ fontWeight: 900, fontSize: 24, lineHeight: 1.2 }}>
                {item.name}
              </div>
              <div style={{ fontWeight: 900, fontSize: 22, color: "#2563eb" }}>
                ${price.toFixed(2)}
              </div>
            </div>

            <div
              className="row"
              style={{ marginTop: 10, gap: 8, flexWrap: "wrap" }}
            >
              {categoryId ? (
                <span className="badge">{categoryName}</span>
              ) : null}
              {condition ? <span className="badge">{condition}</span> : null}
              {status ? <span className="badge">{status}</span> : null}
            </div>

            {item.description ? (
              <div style={{ marginTop: 12, lineHeight: 1.6 }}>
                {item.description}
              </div>
            ) : (
              <div className="muted" style={{ marginTop: 12 }}>
                No description provided.
              </div>
            )}

            <div
              className="row"
              style={{ marginTop: 16, gap: 10, flexWrap: "wrap" }}
            >
              {canBuy ? (
                <button
                  className="btnPrimary"
                  onClick={addToCart}
                  disabled={!isAvailable}
                >
                  {isAvailable ? "Add to Cart" : "Not available"}
                </button>
              ) : !currentUser ? (
                <button
                  className="btnPrimary"
                  onClick={() => navigate("/login")}
                >
                  Login to buy
                </button>
              ) : null}
              {canEdit ? (
                <button
                  className="btnSecondary"
                  onClick={() => navigate(`/edit-item/${id}`)}
                >
                  Edit listing
                </button>
              ) : null}

              {canBuy && cartCount ? (
                <span className="badge">In cart: {cartCount}</span>
              ) : null}
            </div>

            <div className="divider" />

            <div className="muted" style={{ fontSize: 13 }}>
              Seller:{" "}
              {sellerName || (sellerId ? `User #${sellerId}` : "Unknown")}
            </div>
          </div>
        </div>
      </div>

      {/* Responsive fallback - make sure the grid looks nice if not messy*/}
      <style>{`
        @media (max-width: 820px) {
          .itemGrid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}
